import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PingPayout } from '../services/api';

const knownStates = new Set([
  'waiting_approval', 'distributing', 'awaiting_payout', 'confirmed',
  'insufficient_funds', 'review_required', 'failed', 'returned'
]);

export function PingPayoutStatus({ payout, canUseDebugBank, showDebugBankLink = true }: { payout: PingPayout; canUseDebugBank: boolean; showDebugBankLink?: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const infoButton = useRef<HTMLButtonElement>(null);
  const [visibility, setVisibility] = useState<'closed' | 'hover' | 'pinned'>('closed');
  const open = visibility !== 'closed';
  const recipientIssue = /^ping_(company|fee)_(account_|kyc_)/.test(payout.errorCode ?? '');
  const denied = /^ping_(company|fee)_recipient_denied$/.test(payout.errorCode ?? '');
  const state = denied ? 'recipient_denied' : knownStates.has(payout.state) ? payout.state : 'unknown';
  const testAction = !recipientIssue && payout.debugBankAvailable && ['waiting_approval', 'awaiting_payout'].includes(state);
  const bankAccessible = payout.debugBankAccessible ?? (canUseDebugBank && testAction);
  const canOpenDebugBank = showDebugBankLink && payout.mode === 'sandbox' && bankAccessible;

  useEffect(() => { setVisibility('closed'); }, [payout.id, payout.state]);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setVisibility('closed');
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  return <div ref={container} className="mb-3"
    onMouseLeave={() => {
      if (!container.current?.contains(document.activeElement)) setVisibility(current => current === 'hover' ? 'closed' : current);
    }}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setVisibility('closed'); }}
    onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); infoButton.current?.focus(); setVisibility('closed'); } }}>
    <div className="d-flex align-items-center gap-1">
      <span role="status" className={state === 'confirmed' ? 'text-success' : 'text-body'}>{t(`pingPayout.states.${state}`)}</span>
      <button ref={infoButton} type="button" className="btn btn-link p-3 flex-shrink-0" aria-label={t('pingPayout.info.label')}
        aria-expanded={open} aria-controls={id} aria-describedby={open ? id : undefined}
        onMouseEnter={() => setVisibility(current => current === 'closed' ? 'hover' : current)}
        onFocus={() => setVisibility(current => current === 'closed' ? 'hover' : current)}
        onClick={() => setVisibility(current => current === 'pinned' ? 'closed' : 'pinned')}>
        <i className="bi bi-info-circle text-primary" aria-hidden="true" />
      </button>
    </div>
    {recipientIssue && <p className="alert alert-warning mt-2 mb-0" role="alert">{t(payout.errorCode?.startsWith('ping_fee_') ? 'pingOnboarding.payoutFee' : 'pingOnboarding.payoutCompany')}</p>}
    {payout.errorCode === 'ping_debug_bank_uncertain' && <p className="alert alert-warning mt-2 mb-0" role="alert">{t('pingPayout.uncertain')}</p>}
    {payout.errorCode === 'ping_payout_disbursement_failed' && <p className="alert alert-warning mt-2 mb-0" role="alert">{t('pingOnboarding.disbursementFailed')}</p>}
    {open && <div id={id} role="region" aria-label={t('pingPayout.info.label')} className="bg-light border rounded p-3 text-break">
      {payout.errorCode === 'ping_payout_status_unavailable' && <p className="alert alert-warning">{t('pingPayout.info.statusCheckUnavailable')}</p>}
      <p className="fw-semibold mb-1">{t('pingPayout.info.reasonTitle')}</p>
      <p>{t(`pingPayout.info.reason.${state}`)}</p>
      <p className="fw-semibold mb-1">{t('pingPayout.info.waitTitle')}</p>
      <p className="mb-0">{t(`pingPayout.info.wait.${testAction ? 'testAction' : state}`)}</p>
      {testAction && <>
        <p className="mt-2 mb-0">{t(`pingPayout.testNextStep.${state}`)}</p>
        {!bankAccessible && <p className="small mt-2 mb-0">{t('pingPayout.testBankAccess')}</p>}
      </>}
      {canOpenDebugBank && <Link className="btn btn-outline-primary btn-lg w-100 mt-3 text-wrap" to={`/debug-bank/payout/${encodeURIComponent(payout.id)}`}>
        {t('pingPayout.openBank')}
      </Link>}
    </div>}
  </div>;
}
